/**
 * Payload / validation for POST /newHotelOrder (quote link) — no price recalculation.
 */

import { buildInitialHotelPassengerRoomSlots } from "./bookingPassengerSnapshot";
import { roundMoney2 } from "../pricing/pricingEngine";
import { resolveCustomerFacingHotelLineIlsFromOrder } from "./hotelBookingPriceSsot";
import { isAgentHrCustomerFacingSsotOrder } from "./resolvePassengerHotelCustomerFacingDisplay";
import {
  isMultiRoomHotelOrderForPriceChain,
  traceHotelMultiRoomPriceChainAtCheckpoint,
} from "./hotelBooking/traceHotelMultiRoomPriceChain";

export const HOTEL_QUOTE_LINK_CREATE_PAYLOAD_TRACE =
  "[HOTEL_QUOTE_LINK_CREATE_PAYLOAD_TRACE]";
export const HOTEL_QUOTE_LINK_VALIDATION_TRACE =
  "[HOTEL_QUOTE_LINK_VALIDATION_TRACE]";
export const HOTEL_QUOTE_LINK_MODE_TRACE = "[HOTEL_QUOTE_LINK_MODE_TRACE]";

/** @typedef {'quote_link' | 'booking_payment'} HotelOrderValidationMode */

export const HOTEL_ORDER_VALIDATION_MODE_QUOTE_LINK = "quote_link";
export const HOTEL_ORDER_VALIDATION_MODE_BOOKING_PAYMENT = "booking_payment";

const QUOTE_LINK_REQUIRED_FIELDS = [
  "selectedHotelPackage",
  "packageId",
  "hotelId",
  "checkIn",
  "checkOut",
  "ourPrice",
  "session",
  "agentID",
];

function nonEmptyStr(v) {
  return v != null && String(v).trim() !== "";
}

/**
 * @param {object|null|undefined} order
 */
export function pickHotelOrderPackageId(order) {
  const pkg = order?.selectedHotelPackage;
  if (!pkg || typeof pkg !== "object") return null;
  return pkg.packageID ?? pkg.PackageId ?? pkg.packageId ?? null;
}

/**
 * Occupancy slots for quote-link API (counts only — names optional).
 * @param {unknown} rooms
 */
export function buildQuoteLinkPassengerRoomsInput(rooms) {
  return buildInitialHotelPassengerRoomSlots(rooms);
}

/**
 * Contact for quote-link order row — proposal/agent fallbacks; no passenger form required.
 */
export function resolveQuoteLinkOrderContact({
  clientsInfo,
  savedClientInfo,
  leadPassengerSlot,
  loggedUser,
  proposalFirstName,
  proposalLastName,
}) {
  const ci = clientsInfo && typeof clientsInfo === "object" ? clientsInfo : {};
  const sv = savedClientInfo && typeof savedClientInfo === "object" ? savedClientInfo : {};
  const agent =
    loggedUser && typeof loggedUser === "object" ? loggedUser : {};

  const proposalName = [proposalFirstName, proposalLastName]
    .filter((x) => nonEmptyStr(x))
    .map((x) => String(x).trim())
    .join(" ");

  const leadFn = leadPassengerSlot
    ? [leadPassengerSlot.firstname, leadPassengerSlot.lastname]
        .filter((x) => nonEmptyStr(x))
        .map((x) => String(x).trim())
        .join(" ")
    : "";

  const ciName = [ci.firstName, ci.lastName]
    .filter((x) => nonEmptyStr(x))
    .map((x) => String(x).trim())
    .join(" ");

  const fullname =
    (nonEmptyStr(proposalName) ? proposalName : null) ||
    (nonEmptyStr(ciName) ? ciName : null) ||
    (nonEmptyStr(ci.fullname) ? String(ci.fullname).trim() : null) ||
    (nonEmptyStr(leadFn) ? leadFn : null) ||
    (nonEmptyStr(sv.fullname) ? String(sv.fullname).trim() : null) ||
    (nonEmptyStr(agent.fullname) ? String(agent.fullname).trim() : null) ||
    (nonEmptyStr(agent.name) ? String(agent.name).trim() : null) ||
    "לקוח";

  const email =
    (nonEmptyStr(ci.email) ? String(ci.email).trim() : null) ||
    (nonEmptyStr(sv.email) ? String(sv.email).trim() : null) ||
    (nonEmptyStr(leadPassengerSlot?.email) ? String(leadPassengerSlot.email).trim() : null) ||
    (nonEmptyStr(agent.email) ? String(agent.email).trim() : null) ||
    "quote-link@pending.hotelsriver.com";

  const cell =
    (nonEmptyStr(ci.phone) ? String(ci.phone).trim() : null) ||
    (nonEmptyStr(ci.cell) ? String(ci.cell).trim() : null) ||
    (nonEmptyStr(sv.phone) ? String(sv.phone).trim() : null) ||
    (nonEmptyStr(leadPassengerSlot?.cell) ? String(leadPassengerSlot.cell).trim() : null) ||
    (nonEmptyStr(agent.phone) ? String(agent.phone).trim() : null) ||
    (nonEmptyStr(agent.cell) ? String(agent.cell).trim() : null) ||
    "0500000000";

  return { fullname, email, cell };
}

/**
 * @param {{
 *   mode: HotelOrderValidationMode,
 *   currentHotelOrder: object|null,
 *   bookingSnapshot?: object|null,
 *   fullPriceFallback?: number|null,
 *   session?: string|null,
 *   agentId?: number|null,
 *   roomName?: string|null,
 * }} opts
 */
export function validateHotelOrderAction(opts) {
  const mode = opts.mode;
  const order = opts.currentHotelOrder;
  const missingFields = [];
  const requiredFields =
    mode === HOTEL_ORDER_VALIDATION_MODE_QUOTE_LINK
      ? [...QUOTE_LINK_REQUIRED_FIELDS]
      : ["passengerDetailsComplete"];

  const passengersValidationSkipped =
    mode === HOTEL_ORDER_VALIDATION_MODE_QUOTE_LINK;

  const pkg = order?.selectedHotelPackage;
  const packageId = pickHotelOrderPackageId(order);
  const hotelId =
    order?.selectedHotel?.HotelId ??
    order?.selectedHotel?.hotelId ??
    order?.selectedHotel?.Result?.[0]?.HotelId ??
    order?.searchPhrase?.hotelID ??
    null;

  const checkIn = order?.searchPhrase?.checkIn;
  const checkOut = order?.searchPhrase?.checkOut;
  const session =
    opts.session ??
    order?.searchPhrase?.session ??
    null;

  const snap =
    order?.pricingSnapshot ??
    order?.selectedPriceSnapshot?.pricingSnapshot ??
    null;
  const hasPricingSnapshot = Boolean(snap && typeof snap === "object");
  const hasOurPrice = Boolean(order?.ourPrice && typeof order.ourPrice === "object");
  const hasBookingSsot =
    order?.bookingSsotIls != null && Number.isFinite(Number(order.bookingSsotIls));

  const transport = buildHotelNewOrderTransportFields(
    order,
    opts.fullPriceFallback ?? null
  );

  const hasSelectedPackage = Boolean(pkg && packageId);

  if (!hasSelectedPackage) missingFields.push("selectedHotelPackage");
  if (!packageId) missingFields.push("packageId");
  if (!hotelId) missingFields.push("hotelId");
  if (!nonEmptyStr(checkIn)) missingFields.push("checkIn");
  if (!nonEmptyStr(checkOut)) missingFields.push("checkOut");
  if (!nonEmptyStr(session)) missingFields.push("session");
  if (!opts.agentId) missingFields.push("agentID");
  if (!hasOurPrice) missingFields.push("ourPrice");
  if (!transport.ok) missingFields.push("price");
  if (
    mode === HOTEL_ORDER_VALIDATION_MODE_QUOTE_LINK &&
    !hasPricingSnapshot &&
    !hasBookingSsot
  ) {
    missingFields.push("pricingSnapshot");
  }

  if (mode === HOTEL_ORDER_VALIDATION_MODE_BOOKING_PAYMENT) {
    const snapComplete = opts.bookingSnapshot?.isComplete === true;
    if (!snapComplete) {
      missingFields.push("passengerDetails");
    }
  }

  const ok = missingFields.length === 0 && (mode === HOTEL_ORDER_VALIDATION_MODE_QUOTE_LINK ? transport.ok : true);

  if (isMultiRoomHotelOrderForPriceChain(order)) {
    const hotelLineSsot = resolveCustomerFacingHotelLineIlsFromOrder(order);
    const agentHrOurPrice = resolveAgentHrOurPriceForBookingPayload(
      order,
      opts.fullPriceFallback ?? null
    );
    const orderPayloadHotelLine =
      agentHrOurPrice?.priceWithCommission ?? hotelLineSsot;
    traceHotelMultiRoomPriceChainAtCheckpoint(order, "order_validator", {
      profileCardTotal: order?.multiRoomPriceChain?.profileCardTotal,
      displayedPriceAtClick: order?.displayedPriceAtClick,
      availabilityCheckTotal: order?.multiRoomPriceChain?.availabilityCheckTotal,
      passengerPageTotal: hotelLineSsot,
      paymentPageTotal: hotelLineSsot,
      orderPayloadTotal: orderPayloadHotelLine,
      validatorExpectedTotal: orderPayloadHotelLine,
    });
  }

  return {
    ok,
    mode,
    requiredFields,
    missingFields,
    passengersValidationSkipped,
    hasSelectedPackage,
    hasPricingSnapshot: hasPricingSnapshot || hasBookingSsot,
    hasOurPrice,
    transportOk: transport.ok,
    packageId,
    hotelId,
    roomName: opts.roomName ?? null,
    transportErrorHe: transport.ok ? null : transport.errorHe,
  };
}

export function logHotelQuoteLinkValidationTrace(payload) {
  if (typeof import.meta !== "undefined" && !import.meta.env?.DEV) return;
  try {
    // eslint-disable-next-line no-console
    console.info(HOTEL_QUOTE_LINK_VALIDATION_TRACE, payload);
  } catch {
    /* ignore */
  }
}

export function logHotelQuoteLinkModeTrace(payload) {
  if (typeof import.meta !== "undefined" && !import.meta.env?.DEV) return;
  try {
    // eslint-disable-next-line no-console
    console.info(HOTEL_QUOTE_LINK_MODE_TRACE, payload);
  } catch {
    /* ignore */
  }
}

/**
 * @param {boolean} isOffer
 * @returns {HotelOrderValidationMode}
 */
export function resolveHotelOrderValidationModeFromIsOffer(isOffer) {
  return isOffer
    ? HOTEL_ORDER_VALIDATION_MODE_QUOTE_LINK
    : HOTEL_ORDER_VALIDATION_MODE_BOOKING_PAYMENT;
}

/**
 * Agent HR — align booking payload ourPrice with bookingSsotIls (not stale supplier quote).
 * @param {object|null|undefined} order
 * @param {number|null|undefined} fullPriceFallback
 * @returns {object|null}
 */
export function resolveAgentHrOurPriceForBookingPayload(order, fullPriceFallback = null) {
  if (!order || typeof order !== "object") return null;
  const bookingSsotIls = resolveCustomerFacingHotelLineIlsFromOrder(order);
  if (!isAgentHrCustomerFacingSsotOrder(order) || bookingSsotIls == null) {
    return null;
  }

  const op =
    order.ourPrice && typeof order.ourPrice === "object"
      ? order.ourPrice
      : order.selectedHotelPackage?.ourPrice && typeof order.selectedHotelPackage.ourPrice === "object"
        ? order.selectedHotelPackage.ourPrice
        : {};

  const baseIls = roundMoney2(bookingSsotIls);
  const totalNum = Number(
    fullPriceFallback ??
      op.priceWithCreditCardFee ??
      op.customerDisplayTotalIls ??
      Math.ceil(baseIls)
  );
  if (!Number.isFinite(totalNum) || totalNum <= 0) return null;

  return {
    ...op,
    supplierNet:
      op.supplierNet ??
      order.pricingSnapshot?.supplierAmount ??
      order.selectedPriceSnapshot?.pricingSnapshot?.supplierAmount ??
      null,
    priceWithCommission: baseIls,
    pricingSource: "agent_hotel_customer_facing_ssot",
    priceWithCreditCardFee: totalNum,
    priceWithCommissionAndCreditCardFee: totalNum,
    customerDisplayTotalIls: totalNum,
    originalCurrency: op.originalCurrency ?? "ILS",
  };
}

/**
 * Map existing ourPrice fields to API transport strings (no SSOT recalculation).
 * @param {object|null|undefined} order
 * @param {number|null|undefined} fullPriceFallback — already computed display total from order flow
 */
export function buildHotelNewOrderTransportFields(order, fullPriceFallback = null) {
  const agentHrOurPrice = resolveAgentHrOurPriceForBookingPayload(order, fullPriceFallback);
  const op = agentHrOurPrice ?? order?.ourPrice;
  if (!op || typeof op !== "object") {
    return { ok: false, errorHe: "חסר ourPrice בהזמנה — נא לבחור חדר מחדש." };
  }

  const rawTotal =
    op.priceWithCreditCardFee ??
    op.priceWithHandleFee ??
    op.customerDisplayTotalIls ??
    fullPriceFallback;
  const totalNum = Number(rawTotal);
  if (!Number.isFinite(totalNum) || totalNum <= 0) {
    return {
      ok: false,
      errorHe: "מחיר totalPrice לא תקין — נא לרענן את העמוד או לבחור חדר מחדש.",
    };
  }

  const cardNum = Number(op.creditCardFee ?? 0);
  const commissionRaw = op.hrCommission ?? op.hrCommissionPercent ?? 0;
  const commissionNum = Number(commissionRaw);

  return {
    ok: true,
    totalPrice: totalNum.toFixed(2),
    cardFee: (Number.isFinite(cardNum) ? cardNum : 0).toFixed(2),
    commission: Number.isFinite(commissionNum) ? commissionNum : 0,
    handleFee: Number(op.handlingFee ?? op.handleFeeData?.value ?? 0) || 0,
    handleFeeOption: op.handleFeeData?.type === "%" ? "%" : "+",
  };
}

/**
 * Lean offer for DB persist — keeps pricing fields, drops heavy duplicates.
 */
export function buildOfferForNewHotelOrderPersist(orderLike) {
  if (!orderLike || typeof orderLike !== "object") return {};
  const {
    selectedHotel,
    selectedHotelPackage,
    selectedRoom,
    searchPhrase,
    ourPrice,
    passangerData,
    bookingSsotIls,
    selectedPriceSnapshot,
    pricingSnapshot,
    pricePipelineMode,
    clientsInfo,
    customerFacingPackageTotalIls,
    originalCurrency,
    refundability,
  } = orderLike;

  const hotelLean =
    selectedHotel && typeof selectedHotel === "object"
      ? {
          HotelId:
            selectedHotel.HotelId ??
            selectedHotel.hotelId ??
            selectedHotel.Result?.[0]?.HotelId,
          HotelName: selectedHotel.HotelName ?? selectedHotel.hotelName,
          Address: selectedHotel.Address ?? selectedHotel.address,
          Rooms: selectedHotel.Rooms,
          Result: Array.isArray(selectedHotel.Result)
            ? selectedHotel.Result.map((r) => ({
                PackageId: r?.PackageId ?? r?.packageID ?? r?.packageId,
                HotelId: r?.HotelId ?? r?.hotelId,
              }))
            : selectedHotel.Result,
        }
      : selectedHotel;

  return {
    selectedHotel: hotelLean,
    selectedHotelPackage,
    selectedRoom,
    searchPhrase,
    ourPrice,
    passangerData,
    bookingSsotIls,
    selectedPriceSnapshot,
    pricingSnapshot,
    pricePipelineMode,
    clientsInfo,
    customerFacingPackageTotalIls,
    originalCurrency,
    refundability,
  };
}

/**
 * @param {object} payload
 * @param {{ mode?: HotelOrderValidationMode, bookingSnapshot?: object }} [ctx]
 */
export function validateHotelQuoteLinkCreatePayload(payload, ctx = {}) {
  const mode = ctx.mode ?? HOTEL_ORDER_VALIDATION_MODE_QUOTE_LINK;
  const missingRequiredKeys = [];
  const invalidFields = [];

  const quoteLinkMode = mode === HOTEL_ORDER_VALIDATION_MODE_QUOTE_LINK;

  const rootKeys = quoteLinkMode
    ? [
        "session",
        "checkIn",
        "checkOut",
        "package",
        "agentID",
        "hotel",
        "totalPrice",
        "offer",
      ]
    : [
        "session",
        "checkIn",
        "checkOut",
        "package",
        "passengers",
        "agentID",
        "hotel",
        "totalPrice",
        "offer",
      ];

  for (const key of rootKeys) {
    if (payload?.[key] === undefined || payload?.[key] === null) {
      missingRequiredKeys.push(key);
    }
  }

  if (!nonEmptyStr(payload?.session)) missingRequiredKeys.push("session(empty)");

  const pkg = payload?.package;
  const packageId =
    pkg?.PackageId ?? pkg?.packageID ?? pkg?.packageId ?? null;
  if (!packageId) missingRequiredKeys.push("package.PackageId");

  const passengers = payload?.passengers;
  const passengersCount = Array.isArray(passengers) ? passengers.length : 0;
  if (!quoteLinkMode && passengersCount < 1) {
    missingRequiredKeys.push("passengers(empty)");
  }

  const hotel = payload?.hotel;
  const hotelId =
    hotel?.HotelId ??
    hotel?.hotelId ??
    hotel?.Result?.[0]?.HotelId ??
    null;
  if (!hotelId) missingRequiredKeys.push("hotel.HotelId");

  const totalNum = Number(payload?.totalPrice);
  if (!Number.isFinite(totalNum) || totalNum <= 0) {
    invalidFields.push("totalPrice");
  }

  const op = payload?.offer?.ourPrice;
  const hasOurPrice = op && typeof op === "object";
  if (!hasOurPrice) missingRequiredKeys.push("offer.ourPrice");

  const snap =
    payload?.offer?.pricingSnapshot ??
    payload?.selectedPriceSnapshot?.pricingSnapshot ??
    payload?.offer?.selectedPriceSnapshot?.pricingSnapshot;
  const hasPricingSnapshot = snap && typeof snap === "object";
  const hasBookingSsot =
    payload?.bookingSsotIls != null &&
    Number.isFinite(Number(payload.bookingSsotIls));

  const passengersValidationSkipped =
    mode === HOTEL_ORDER_VALIDATION_MODE_QUOTE_LINK;

  if (quoteLinkMode && !hasPricingSnapshot && !hasBookingSsot) {
    missingRequiredKeys.push("pricingSnapshot");
  }

  if (mode === HOTEL_ORDER_VALIDATION_MODE_BOOKING_PAYMENT) {
    if (!nonEmptyStr(payload?.fullname)) invalidFields.push("fullname");
    if (!nonEmptyStr(payload?.email)) invalidFields.push("email");
    if (!nonEmptyStr(payload?.cell)) invalidFields.push("cell");
    const bookingSnapshot = ctx.bookingSnapshot;
    if (bookingSnapshot?.isComplete !== true) {
      invalidFields.push("passengerDetails");
    }
  }

  const pkgOffer = payload?.offer?.selectedHotelPackage;
  const hasSelectedPackage =
    pkgOffer && typeof pkgOffer === "object" && (pkgOffer.packageID || pkgOffer.PackageId);

  const ok =
    missingRequiredKeys.length === 0 && invalidFields.length === 0;

  return {
    ok,
    missingRequiredKeys,
    invalidFields,
    meta: {
      mode,
      passengersCount,
      passengersValidationSkipped,
      hasSelectedPackage: Boolean(hasSelectedPackage),
      hasOurPrice,
      hasPricingSnapshot,
      packageId,
      hotelId,
      payloadKeys: payload && typeof payload === "object" ? Object.keys(payload) : [],
    },
  };
}

export function logHotelQuoteLinkCreatePayloadTrace(payload) {
  if (typeof import.meta !== "undefined" && !import.meta.env?.DEV) return;
  try {
    // eslint-disable-next-line no-console
    console.info(HOTEL_QUOTE_LINK_CREATE_PAYLOAD_TRACE, payload);
  } catch {
    /* ignore */
  }
}
